import { ReactElement, useEffect, useRef, Fragment } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

// @ts-expect-error - plotly.js-dist-min doesn't have proper type declarations
import Plotly from 'plotly.js-dist-min';
import {
  articlesAPI,
  ArticleContent,
} from '../../services/api';
import {
  ArrowLeft,
  Calendar,
  Cpu,
  AlertTriangle,
  Lightbulb,
  TrendingUp,
  ChevronRight,
  Loader2,
  FileText,
  BarChart3,
  Table2,
  Building2,
  DollarSign,
  LineChart,
} from 'lucide-react';

// ============================================================================
// Chart Component - Renders Plotly charts
// ============================================================================
interface ChartProps {
  title: string;
  description: string;
  plotlyConfig: Record<string, unknown>;
}

function Chart({ title, description, plotlyConfig }: ChartProps): ReactElement {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const chartElement = chartRef.current;

    if (chartElement && plotlyConfig && Object.keys(plotlyConfig).length > 0) {
      const data = plotlyConfig.data as Plotly.Data[];
      const layout = {
        ...(plotlyConfig.layout as Partial<Plotly.Layout>),
        paper_bgcolor: 'rgba(0,0,0,0)',
        plot_bgcolor: 'rgba(15, 23, 42, 0.5)',
        font: { color: '#94a3b8', size: 11 },
        margin: { t: 40, r: 20, b: 40, l: 50 },
        autosize: true,
      };
      const config: Partial<Plotly.Config> = {
        responsive: true,
        displayModeBar: false,
      };

      Plotly.newPlot(chartElement, data, layout, config);
    }

    return () => {
      if (chartElement) {
        Plotly.purge(chartElement);
      }
    };
  }, [plotlyConfig]);

  if (!plotlyConfig || Object.keys(plotlyConfig).length === 0) {
    return <></>;
  }

  return (
    <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-700/50">
        <h4 className="text-sm font-medium text-slate-200">{title}</h4>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>
      <div ref={chartRef} className="w-full h-80" />
    </div>
  );
}

// ============================================================================
// Data Table Component
// ============================================================================
interface DataTableProps {
  columns: string[];
  rows: Array<Record<string, unknown>>;
}

function DataTable({ columns, rows }: DataTableProps): ReactElement {
  if (!rows || rows.length === 0) return <></>;

  // Get row keys from first row
  const rowKeys = Object.keys(rows[0]).filter(k => k !== 'company' && k !== 'symbol');

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-700">
            {columns.map((col, i) => (
              <th
                key={i}
                className="px-3 py-2 text-left text-xs font-medium text-slate-400 uppercase tracking-wider"
              >
                {col}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {rows.map((row, rowIdx) => (
            <tr key={rowIdx} className="hover:bg-slate-800/50 transition-colors">
              <td className="px-3 py-2 font-medium text-slate-200">
                {String(row.company || row.symbol || '')}
              </td>
              {rowKeys.map((key, cellIdx) => (
                <td key={cellIdx} className="px-3 py-2 text-slate-300 font-mono text-xs">
                  {String(row[key] ?? 'N/A')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Performance Table Component
// ============================================================================
interface PerformanceDataItem {
  company: string;
  symbol: string;
  returns: Record<string, { formatted?: string; value?: number }>;
  risk_metrics: Record<string, { formatted?: string; value?: number }>;
}

interface PerformanceTableProps {
  data: PerformanceDataItem[];
}

function PerformanceTable({ data }: PerformanceTableProps): ReactElement {
  if (!data || data.length === 0) return <></>;

  return (
    <div className="space-y-6">
      {/* Returns Table */}
      <div>
        <h5 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-400" />
          Period Returns
        </h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Company</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">1M</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">3M</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">6M</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">12M</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">YTD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-800/50">
                  <td className="px-3 py-2 font-medium text-slate-200">{item.company}</td>
                  {['1m', '3m', '6m', '12m', 'ytd'].map((period) => {
                    const val = item.returns[period];
                    const formatted = val?.formatted || 'N/A';
                    const numVal = val?.value || 0;
                    const colorClass = numVal > 0 ? 'text-emerald-400' : numVal < 0 ? 'text-red-400' : 'text-slate-400';
                    return (
                      <td key={period} className={`px-3 py-2 text-right font-mono text-xs ${colorClass}`}>
                        {formatted}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Risk Metrics Table */}
      <div>
        <h5 className="text-sm font-medium text-slate-300 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-400" />
          Risk Metrics
        </h5>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Company</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">Volatility</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">Sharpe</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">Beta</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">Alpha</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-slate-400">Max DD</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {data.map((item, idx) => (
                <tr key={idx} className="hover:bg-slate-800/50">
                  <td className="px-3 py-2 font-medium text-slate-200">{item.company}</td>
                  {['volatility', 'sharpe_ratio', 'beta', 'alpha', 'max_drawdown'].map((metric) => {
                    const val = item.risk_metrics[metric];
                    return (
                      <td key={metric} className="px-3 py-2 text-right font-mono text-xs text-slate-300">
                        {val?.formatted || 'N/A'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Company Highlights Component
// ============================================================================
interface CompanyData {
  company_name: string;
  symbol: string;
  financial: Record<string, { formatted?: string; classification?: string; trend?: string }>;
  market: Record<string, { formatted?: string; classification?: string }>;
  institutional: Record<string, { formatted?: string; classification?: string }>;
  insider: Record<string, { formatted?: string }>;
}

interface CompanyHighlightsProps {
  companies: CompanyData[];
  narratives?: Record<string, string>;
}

function CompanyHighlights({ companies, narratives }: CompanyHighlightsProps): ReactElement {
  if (!companies || companies.length === 0) return <></>;

  return (
    <div className="space-y-6">
      {companies.map((company) => (
        <div key={company.symbol} className="bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden">
          {/* Company Header */}
          <div className="px-4 py-3 border-b border-slate-700/50 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h4 className="text-base font-semibold text-slate-200">{company.company_name}</h4>
                <span className="text-xs font-mono text-amber-400">{company.symbol}</span>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Financial */}
            <div>
              <h5 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Financial</h5>
              <div className="space-y-1.5">
                <MetricRow label="Revenue" value={company.financial?.revenue} />
                <MetricRow label="Growth YoY" value={company.financial?.revenue_yoy} />
                <MetricRow label="Net Margin" value={company.financial?.net_margin} />
                <MetricRow label="ROE" value={company.financial?.roe} />
              </div>
            </div>

            {/* Market */}
            <div>
              <h5 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Market</h5>
              <div className="space-y-1.5">
                <MetricRow label="12M Return" value={company.market?.return_12m} />
                <MetricRow label="Volatility" value={company.market?.volatility} />
                <MetricRow label="Sharpe" value={company.market?.sharpe_ratio} />
                <MetricRow label="Beta" value={company.market?.beta} />
              </div>
            </div>

            {/* Institutional */}
            <div>
              <h5 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Institutional</h5>
              <div className="space-y-1.5">
                <MetricRow label="Ownership" value={company.institutional?.ownership_percent} />
                <MetricRow label="Change" value={company.institutional?.ownership_change} />
              </div>
            </div>

            {/* Insider */}
            <div>
              <h5 className="text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-2">Insider</h5>
              <div className="space-y-1.5">
                <MetricRow label="Sentiment" value={company.insider?.sentiment} />
                <MetricRow label="Buy/Sell" value={company.insider?.buy_sell_ratio} />
              </div>
            </div>
          </div>

          {/* Narrative (if present) */}
          {narratives && narratives[company.symbol] && (
            <div className="px-4 pb-4">
              <div className="bg-slate-800/50 rounded-lg p-4">
                <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                  {narratives[company.symbol]}
                </p>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MetricRow({ label, value }: { label: string; value?: { formatted?: string; classification?: string; trend?: string } }): ReactElement {
  const formatted = value?.formatted || 'N/A';
  const classification = value?.classification;

  let classificationColor = 'text-slate-500';
  if (classification) {
    if (classification.toLowerCase().includes('strong') || classification.toLowerCase().includes('high') || classification.toLowerCase().includes('positive')) {
      classificationColor = 'text-emerald-400';
    } else if (classification.toLowerCase().includes('weak') || classification.toLowerCase().includes('low') || classification.toLowerCase().includes('negative')) {
      classificationColor = 'text-red-400';
    } else if (classification.toLowerCase().includes('moderate') || classification.toLowerCase().includes('neutral')) {
      classificationColor = 'text-amber-400';
    }
  }

  return (
    <div className="flex items-center justify-between">
      <span className="text-xs text-slate-500">{label}</span>
      <div className="text-right">
        <span className="text-xs font-mono text-slate-300">{formatted}</span>
        {classification && (
          <span className={`ml-1.5 text-[10px] ${classificationColor}`}>({classification})</span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Section 02 Subsection Component
// ============================================================================
interface Section02SubsectionProps {
  narratives: Record<string, unknown>;
  subsection: Record<string, unknown>;
}

function Section02Subsection({ narratives, subsection }: Section02SubsectionProps): ReactElement {
  // Get data from subsection
  const data = subsection.data as Record<string, unknown> | undefined;
  const companies = data?.companies as Array<Record<string, unknown>> | undefined;

  // Render all narrative fields (only strings)
  const narrativeEntries: Array<[string, string]> = Object.entries(narratives)
    .filter((entry): entry is [string, string] =>
      typeof entry[1] === 'string' && entry[0] !== 'error' && entry[0] !== 'raw_response'
    );

  // Get intro narrative safely
  const introNarrative = typeof narratives.intro_narrative === 'string' ? narratives.intro_narrative : null;

  // Get key takeaways safely
  const keyTakeaways = Array.isArray(narratives.key_takeaways)
    ? (narratives.key_takeaways as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];

  // Get portfolio considerations safely
  const portfolioConsiderations = Array.isArray(narratives.portfolio_considerations)
    ? (narratives.portfolio_considerations as unknown[]).filter((t): t is string => typeof t === 'string')
    : [];

  return (
    <div className="space-y-4">
      {/* Intro narrative */}
      {introNarrative && (
        <div className="bg-slate-800/30 rounded-lg p-4">
          <p className="text-slate-300 leading-relaxed">{introNarrative}</p>
        </div>
      )}

      {/* Companies data table */}
      {companies && companies.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-700/50">
            <h4 className="text-sm font-medium text-slate-200">Company Data</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Company</th>
                  {Object.keys(companies[0] || {})
                    .filter(k => !['company_name', 'symbol', 'company', 'name'].includes(k))
                    .slice(0, 6)
                    .map(key => (
                      <th key={key} className="px-3 py-2 text-left text-xs font-medium text-slate-400 capitalize">
                        {key.replace(/_/g, ' ')}
                      </th>
                    ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {companies.map((company, idx) => {
                  const companyName = String(company.company_name || company.name || company.symbol || '');
                  const symbol = company.symbol ? String(company.symbol) : null;
                  return (
                    <tr key={idx} className="hover:bg-slate-800/50">
                      <td className="px-3 py-2 font-medium text-slate-200">
                        {companyName}
                        {symbol && (
                          <span className="ml-2 text-xs font-mono text-amber-400">{symbol}</span>
                        )}
                      </td>
                      {Object.entries(company)
                        .filter(([k]) => !['company_name', 'symbol', 'company', 'name'].includes(k))
                        .slice(0, 6)
                        .map(([key, value]) => (
                          <td key={key} className="px-3 py-2 text-slate-300 font-mono text-xs">
                            {formatCellValue(value)}
                          </td>
                        ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analysis narratives */}
      {narrativeEntries
        .filter(([key]) => key !== 'intro_narrative')
        .map(([key, value]) => (
          <div key={key} className="bg-slate-800/30 rounded-lg p-4">
            <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
              {key.replace(/_/g, ' ').replace(/narrative|analysis|context/gi, '').trim() || 'Analysis'}
            </h5>
            <p className="text-sm text-slate-300 leading-relaxed">{value}</p>
          </div>
        ))}

      {/* Key takeaways if present */}
      {keyTakeaways.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-emerald-400 uppercase tracking-wider mb-3">
            Key Takeaways
          </h5>
          <ul className="space-y-2">
            {keyTakeaways.map((takeaway, idx) => (
              <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                <ChevronRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{takeaway}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Portfolio considerations if present */}
      {portfolioConsiderations.length > 0 && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-3">
            Considerations
          </h5>
          <ul className="space-y-2">
            {portfolioConsiderations.map((item, idx) => (
              <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return 'N/A';
  if (typeof value === 'number') {
    if (Math.abs(value) >= 1e9) return `${(value / 1e9).toFixed(1)}B`;
    if (Math.abs(value) >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
    if (Math.abs(value) < 0.01 && value !== 0) return value.toFixed(4);
    if (Number.isInteger(value)) return value.toString();
    return value.toFixed(2);
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
}

// ============================================================================
// Section 03 Subsection Renderer
// ============================================================================
interface Section03SubsectionProps {
  narratives: Record<string, unknown>;
  subsection: {
    id: string;
    name: string;
    data?: Record<string, unknown>;
    charts?: Array<Record<string, unknown>>;
    [key: string]: unknown;
  };
}

function Section03Subsection({ narratives, subsection }: Section03SubsectionProps): ReactElement {
  const subsectionId = subsection.id;

  // Section 3F has different narrative structure
  const is3F = subsectionId === '3F';
  const is3B = subsectionId === '3B';
  const narrativeText = typeof narratives.narrative === 'string' ? narratives.narrative : '';

  // New: intro_narrative (aligned with Section 02)
  const introNarrative = typeof narratives.intro_narrative === 'string' ? narratives.intro_narrative : null;

  // 3B specific: overview and category narratives
  const overviewText = typeof narratives.overview === 'string' ? narratives.overview : '';
  const rawCategoryNarratives = narratives.category_narratives;
  const categoryNarratives: Record<string, string> | null =
    (rawCategoryNarratives && typeof rawCategoryNarratives === 'object' && !Array.isArray(rawCategoryNarratives))
      ? (rawCategoryNarratives as Record<string, string>)
      : null;
  const executiveSummary = typeof narratives.executive_summary === 'string' ? narratives.executive_summary : '';
  const strengthsNarrative = typeof narratives.strengths_narrative === 'string' ? narratives.strengths_narrative : '';
  const concernsNarrative = typeof narratives.concerns_narrative === 'string' ? narratives.concerns_narrative : '';
  const opportunitiesNarrative = typeof narratives.opportunities_narrative === 'string' ? narratives.opportunities_narrative : '';
  const recommendationsNarrative = typeof narratives.recommendations_narrative === 'string'
    ? narratives.recommendations_narrative
    : (typeof narratives.recommendations_narratives === 'string' ? narratives.recommendations_narratives : '');

  const keyFindings: string[] = Array.isArray(narratives.key_findings) ? (narratives.key_findings as string[]) : [];
  const keyTakeaways: string[] = Array.isArray(narratives.key_takeaways) ? (narratives.key_takeaways as string[]) : [];
  const rawHighlights = narratives.company_highlights;
  const companyHighlights: Record<string, unknown> | null =
    (rawHighlights && typeof rawHighlights === 'object' && !Array.isArray(rawHighlights))
      ? (rawHighlights as Record<string, unknown>)
      : null;
  const data = subsection.data || {};

  // Section 3B has charts
  const isChartSection = subsectionId === '3B';
  const rawCharts = data.charts;
  // Charts can be an array or a dict - convert dict to array if needed
  const charts: Array<Record<string, unknown>> | null = (() => {
    if (Array.isArray(rawCharts)) return rawCharts;
    if (rawCharts && typeof rawCharts === 'object') {
      // Convert dict to array of chart objects
      return Object.values(rawCharts as Record<string, unknown>).filter(
        (v): v is Record<string, unknown> => v !== null && typeof v === 'object'
      );
    }
    return null;
  })();

  // Get company data from various subsection structures
  const rawCompanies = data.companies || data.financial_histories || data.growth_metrics;
  const companies: Array<Record<string, unknown>> = Array.isArray(rawCompanies) ? rawCompanies : [];

  // Helper to render highlight value (handles strings, arrays, and metric objects)
  const renderHighlightContent = (value: unknown): ReactElement => {
    if (typeof value === 'string') {
      return <span>{value}</span>;
    }
    if (value === null || value === undefined) {
      return <span className="text-slate-500 italic">No data</span>;
    }
    if (Array.isArray(value)) {
      // Array of strings or items
      return (
        <ul className="list-disc list-inside space-y-1">
          {value.map((item, i) => (
            <li key={i}>{typeof item === 'string' ? item : JSON.stringify(item)}</li>
          ))}
        </ul>
      );
    }
    if (typeof value === 'object') {
      const obj = value as Record<string, unknown>;
      // Check for narrative text fields first
      if (obj.text && typeof obj.text === 'string') return <span>{obj.text}</span>;
      if (obj.summary && typeof obj.summary === 'string') return <span>{obj.summary}</span>;
      if (obj.narrative && typeof obj.narrative === 'string') return <span>{obj.narrative}</span>;

      // Render as formatted metrics table
      const entries = Object.entries(obj);
      if (entries.length > 0) {
        return (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {entries.map(([key, val]) => (
              <Fragment key={key}>
                <span className="text-slate-400">{key.replace(/_/g, ' ')}</span>
                <span className="text-slate-200 font-mono text-right">
                  {typeof val === 'number' ? val.toFixed(2) : String(val)}
                </span>
              </Fragment>
            ))}
          </div>
        );
      }
    }
    return <span>{String(value)}</span>;
  };

  // Render helpers to ensure proper typing
  const renderCompanyHighlights = (): ReactElement | null => {
    if (companyHighlights === null || Object.keys(companyHighlights).length === 0) return null;
    return (
      <div className="space-y-3">
        <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Company Highlights</h5>
        {Object.entries(companyHighlights).map(([symbol, highlight]) => (
          <div key={symbol} className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className="px-2 py-0.5 text-xs font-mono text-amber-400 bg-amber-500/10 rounded">{symbol}</span>
            </div>
            <div className="text-sm text-slate-300 leading-relaxed">
              {renderHighlightContent(highlight)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCompanyTable = (): ReactElement | null => {
    if (isChartSection || companies.length === 0) return null;
    return (
      <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-700/50">
          <h5 className="text-sm font-medium text-slate-300">Company Data</h5>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="px-3 py-2 text-left text-xs font-medium text-slate-400">Company</th>
                {Object.keys(companies[0] || {})
                  .filter(k => !['company_name', 'symbol', 'name'].includes(k))
                  .slice(0, 6)
                  .map(key => (
                    <th key={key} className="px-3 py-2 text-right text-xs font-medium text-slate-400 capitalize">
                      {key.replace(/_/g, ' ')}
                    </th>
                  ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {companies.map((company, idx) => {
                const rec = company as Record<string, unknown>;
                const companyName = String(rec['company_name'] || rec['name'] || '');
                const symbol = rec['symbol'] ? String(rec['symbol']) : null;
                return (
                  <tr key={idx} className="hover:bg-slate-800/50">
                    <td className="px-3 py-2 font-medium text-slate-200">
                      {companyName}
                      {symbol && <span className="ml-2 text-xs font-mono text-amber-400">{symbol}</span>}
                    </td>
                    {Object.entries(rec)
                      .filter(([k]) => !['company_name', 'symbol', 'name'].includes(k))
                      .slice(0, 6)
                      .map(([key, value]) => (
                        <td key={key} className="px-3 py-2 text-right text-slate-300 font-mono text-xs">
                          {formatCellValue(value)}
                        </td>
                      ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  const renderCharts = (): ReactElement | null => {
    if (!isChartSection || charts === null || charts.length === 0) return null;
    return (
      <div className="grid md:grid-cols-2 gap-4">
        {charts.slice(0, 24).map((chart, idx) => {
          const layout = chart.layout as Record<string, unknown> | undefined;
          const title = layout?.title as { text?: string } | string | undefined;
          const titleText = typeof title === 'object' ? title?.text || `Chart ${idx + 1}` : String(title || `Chart ${idx + 1}`);
          return (
            <Chart
              key={idx}
              title={titleText}
              description=""
              plotlyConfig={chart}
            />
          );
        })}
      </div>
    );
  };

  // Render summary stats card for portfolio-level metrics
  const renderSummaryStats = (): ReactElement | null => {
    // Extract summary stats based on subsection type
    const summaryItems: Array<{ label: string; value: string | number; color?: string }> = [];

    // Section 3A: Revenue & Growth Overview
    if (subsectionId === '3A') {
      const yearsCoverage = data.years_coverage;
      const avgRevenueGrowth = data.avg_revenue_growth;
      const combinedRevenue = data.combined_revenue_formatted;
      const growthDist = data.growth_classification_distribution as Record<string, number> | undefined;

      if (yearsCoverage) summaryItems.push({ label: 'Years of Data', value: String(yearsCoverage) });
      if (combinedRevenue) summaryItems.push({ label: 'Combined Revenue', value: String(combinedRevenue) });
      if (typeof avgRevenueGrowth === 'number') summaryItems.push({
        label: 'Avg Revenue Growth',
        value: `${avgRevenueGrowth.toFixed(1)}%`,
        color: avgRevenueGrowth > 0 ? 'text-emerald-400' : avgRevenueGrowth < 0 ? 'text-red-400' : undefined
      });
      if (growthDist) {
        Object.entries(growthDist).forEach(([key, val]) => {
          summaryItems.push({ label: key.replace(/_/g, ' '), value: `${val} companies` });
        });
      }
    }

    // Section 3B: Market Performance
    if (subsectionId === '3B') {
      const portfolioMetrics = data.portfolio_metrics as Record<string, unknown> | undefined;
      const returnsSummary = data.returns_summary as Record<string, unknown> | undefined;
      const riskSummary = data.risk_summary as Record<string, unknown> | undefined;

      if (portfolioMetrics) {
        Object.entries(portfolioMetrics).slice(0, 4).forEach(([key, val]) => {
          if (typeof val === 'number') {
            const isReturn = key.toLowerCase().includes('return');
            summaryItems.push({
              label: key.replace(/_/g, ' '),
              value: isReturn ? `${val.toFixed(1)}%` : val.toFixed(2),
              color: isReturn ? (val > 0 ? 'text-emerald-400' : val < 0 ? 'text-red-400' : undefined) : undefined
            });
          }
        });
      }

      if (returnsSummary) {
        const avgReturn = returnsSummary.avg_12m_return ?? returnsSummary.avg_return;
        if (typeof avgReturn === 'number') {
          summaryItems.push({
            label: 'Avg 12M Return',
            value: `${avgReturn.toFixed(1)}%`,
            color: avgReturn > 0 ? 'text-emerald-400' : avgReturn < 0 ? 'text-red-400' : undefined
          });
        }
      }

      if (riskSummary) {
        const avgVol = riskSummary.avg_volatility;
        const avgSharpe = riskSummary.avg_sharpe;
        if (typeof avgVol === 'number') summaryItems.push({ label: 'Avg Volatility', value: `${avgVol.toFixed(1)}%` });
        if (typeof avgSharpe === 'number') summaryItems.push({ label: 'Avg Sharpe', value: avgSharpe.toFixed(2) });
      }
    }

    // Section 3C/3D/3E: Working Capital / Capital Allocation / Earnings Quality
    if (['3C', '3D', '3E'].includes(subsectionId)) {
      const portfolioMetrics = data.portfolio_metrics as Record<string, unknown> | undefined;
      const distributions = data.distributions as Record<string, Record<string, number>> | undefined;
      const metricRanges = data.metric_ranges as Record<string, { min?: number; max?: number; avg?: number }> | undefined;

      if (portfolioMetrics) {
        Object.entries(portfolioMetrics).forEach(([key, val]) => {
          if (typeof val === 'number') {
            summaryItems.push({ label: key.replace(/_/g, ' '), value: val.toFixed(2) });
          } else if (typeof val === 'string') {
            summaryItems.push({ label: key.replace(/_/g, ' '), value: val });
          }
        });
      }

      if (metricRanges) {
        Object.entries(metricRanges).slice(0, 3).forEach(([metric, range]) => {
          if (range.avg !== undefined) {
            summaryItems.push({ label: `Avg ${metric.replace(/_/g, ' ')}`, value: range.avg.toFixed(2) });
          }
        });
      }

      if (distributions) {
        Object.entries(distributions).slice(0, 2).forEach(([category, dist]) => {
          const topItem = Object.entries(dist).sort((a, b) => b[1] - a[1])[0];
          if (topItem) {
            summaryItems.push({ label: `Top ${category.replace(/_/g, ' ')}`, value: `${topItem[0]} (${topItem[1]})` });
          }
        });
      }
    }

    // Section 3F: Investment Thesis & Synthesis
    if (subsectionId === '3F') {
      const portfolioMetrics = data.portfolio_metrics as Record<string, unknown> | undefined;
      const avgPiotroski = data.avg_piotroski_f_score;
      const profiles = data.profiles as Array<Record<string, unknown>> | undefined;

      if (typeof avgPiotroski === 'number') {
        summaryItems.push({
          label: 'Avg Piotroski F-Score',
          value: avgPiotroski.toFixed(1),
          color: avgPiotroski >= 7 ? 'text-emerald-400' : avgPiotroski >= 4 ? 'text-amber-400' : 'text-red-400'
        });
      }

      if (portfolioMetrics) {
        Object.entries(portfolioMetrics).slice(0, 4).forEach(([key, val]) => {
          if (typeof val === 'number') {
            summaryItems.push({ label: key.replace(/_/g, ' '), value: val.toFixed(2) });
          } else if (typeof val === 'string') {
            summaryItems.push({ label: key.replace(/_/g, ' '), value: val });
          }
        });
      }

      if (profiles && profiles.length > 0) {
        summaryItems.push({ label: 'Companies Analyzed', value: profiles.length });
      }
    }

    if (summaryItems.length === 0) return null;

    return (
      <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4 mb-4">
        <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Portfolio Summary</h5>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {summaryItems.map((item, idx) => (
            <div key={idx} className="text-center">
              <div className={`text-lg font-semibold ${item.color || 'text-slate-200'}`}>
                {item.value}
              </div>
              <div className="text-xs text-slate-500 capitalize">{item.label}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {/* Summary Stats Card */}
      {renderSummaryStats()}

      {/* Intro Narrative (aligned with Section 02) */}
      {!is3F && introNarrative && (
        <div className="bg-slate-800/30 rounded-lg p-4">
          <p className="text-slate-300 leading-relaxed">{introNarrative}</p>
        </div>
      )}

      {/* Main Narrative (for non-3F sections) */}
      {!is3F && narrativeText && (
        <div className="bg-slate-800/30 rounded-lg p-4">
          <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{narrativeText}</p>
        </div>
      )}

      {/* Section 3F Special Layout */}
      {is3F && (
        <div className="space-y-4">
          {/* Executive Summary */}
          {executiveSummary && (
            <div className="bg-gradient-to-r from-rose-500/10 to-amber-500/10 border border-rose-500/20 rounded-lg p-5">
              <h5 className="text-sm font-semibold text-rose-400 mb-3">Executive Summary</h5>
              <p className="text-slate-300 leading-relaxed">{executiveSummary}</p>
            </div>
          )}

          {/* Strengths */}
          {strengthsNarrative && (
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
              <h5 className="text-xs font-medium text-emerald-400 uppercase tracking-wider mb-2">Strengths</h5>
              <p className="text-sm text-slate-300 leading-relaxed">{strengthsNarrative}</p>
            </div>
          )}

          {/* Concerns */}
          {concernsNarrative && (
            <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
              <h5 className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-2">Concerns</h5>
              <p className="text-sm text-slate-300 leading-relaxed">{concernsNarrative}</p>
            </div>
          )}

          {/* Opportunities */}
          {opportunitiesNarrative && (
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
              <h5 className="text-xs font-medium text-blue-400 uppercase tracking-wider mb-2">Opportunities</h5>
              <p className="text-sm text-slate-300 leading-relaxed">{opportunitiesNarrative}</p>
            </div>
          )}

          {/* Recommendations */}
          {recommendationsNarrative && (
            <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
              <h5 className="text-xs font-medium text-purple-400 uppercase tracking-wider mb-2">Recommendations</h5>
              <p className="text-sm text-slate-300 leading-relaxed">{recommendationsNarrative}</p>
            </div>
          )}

          {/* Key Takeaways for 3F */}
          {keyTakeaways.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-4">
              <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-3">Key Takeaways</h5>
              <ul className="space-y-2">
                {keyTakeaways.map((takeaway, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                    <ChevronRight className="w-4 h-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <span>{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Key Findings (for non-3F sections) */}
      {!is3F && keyFindings.length > 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-emerald-400 uppercase tracking-wider mb-3">Key Findings</h5>
          <ul className="space-y-2">
            {keyFindings.map((finding, idx) => (
              <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                <ChevronRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                <span>{finding}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Section 3B: Overview before charts */}
      {is3B && overviewText && (
        <div className="bg-slate-800/30 rounded-lg p-4">
          <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">{overviewText}</p>
        </div>
      )}

      {/* Non-3F content */}
      {!is3F && renderCompanyHighlights()}
      {!is3F && renderCompanyTable()}
      {!is3F && renderCharts()}

      {/* Section 3B: Category narratives after charts */}
      {is3B && categoryNarratives && Object.keys(categoryNarratives).length > 0 && (
        <div className="space-y-3 mt-4">
          <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Chart Analysis by Category</h5>
          {Object.entries(categoryNarratives).map(([category, text]) => (
            <div key={category} className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
              <h6 className="text-sm font-medium text-blue-400 capitalize mb-2">{category.replace(/_/g, ' ')}</h6>
              <p className="text-sm text-slate-300 leading-relaxed">{String(text)}</p>
            </div>
          ))}
        </div>
      )}

      {/* Chart Context (from chart_context_* fields - aligned with Section 02 prompts) */}
      {(() => {
        const chartContextEntries = Object.entries(narratives)
          .filter(([key, value]) => key.startsWith('chart_context_') && typeof value === 'string')
          .map(([key, value]) => [key.replace('chart_context_', '').replace(/_/g, ' '), value as string] as const);

        if (chartContextEntries.length === 0) return null;

        return (
          <div className="space-y-3 mt-4">
            <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider">Chart Interpretations</h5>
            <div className="grid md:grid-cols-2 gap-3">
              {chartContextEntries.map(([chartName, context]) => (
                <div key={chartName} className="bg-slate-900/50 border border-slate-700/50 rounded-lg p-4">
                  <h6 className="text-sm font-medium text-cyan-400 capitalize mb-2">{chartName}</h6>
                  <p className="text-sm text-slate-300 leading-relaxed">{context}</p>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Additional narratives (for non-3F sections) */}
      {!is3F && narratives.growth_quality_assessment ? (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-blue-400 uppercase tracking-wider mb-2">Growth Quality Assessment</h5>
          <p className="text-sm text-slate-300 leading-relaxed">{String(narratives.growth_quality_assessment)}</p>
        </div>
      ) : null}

      {narratives.efficiency_assessment ? (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-blue-400 uppercase tracking-wider mb-2">Efficiency Assessment</h5>
          <p className="text-sm text-slate-300 leading-relaxed">{String(narratives.efficiency_assessment)}</p>
        </div>
      ) : null}

      {narratives.value_creation_assessment ? (
        <div className="bg-purple-500/5 border border-purple-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-purple-400 uppercase tracking-wider mb-2">Value Creation Assessment</h5>
          <p className="text-sm text-slate-300 leading-relaxed">{String(narratives.value_creation_assessment)}</p>
        </div>
      ) : null}

      {narratives.quality_assessment ? (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg p-4">
          <h5 className="text-xs font-medium text-amber-400 uppercase tracking-wider mb-2">Quality Assessment</h5>
          <p className="text-sm text-slate-300 leading-relaxed">{String(narratives.quality_assessment)}</p>
        </div>
      ) : null}
    </div>
  );
}

// ============================================================================
// Subsection Renderer
// ============================================================================
interface SubsectionData {
  id: string;
  name: string;
  narratives?: Record<string, unknown>;
  companies?: CompanyData[];
  table?: { columns: string[]; rows: Array<Record<string, unknown>> };
  performance_data?: PerformanceDataItem[];
  charts?: Array<{ id: string; title: string; description: string; plotly_config: Record<string, unknown> }>;
  [key: string]: unknown;
}

function SubsectionRenderer({ subsection }: { subsection: SubsectionData }): ReactElement {
  const narratives = subsection.narratives || {};
  const narrativeText: string = typeof narratives.narrative === 'string' ? String(narratives.narrative) : '';

  // Determine subsection type and render accordingly
  const subsectionId = subsection.id;

  return (
    <div className="mb-10">
      {/* Subsection Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center">
          {/* Section 1 icons */}
          {subsectionId === '1.1' && <Building2 className="w-4 h-4 text-blue-400" />}
          {subsectionId === '1.2' && <Table2 className="w-4 h-4 text-emerald-400" />}
          {subsectionId === '1.3' && <TrendingUp className="w-4 h-4 text-purple-400" />}
          {subsectionId === '1.4' && <BarChart3 className="w-4 h-4 text-amber-400" />}
          {subsectionId === '1.5' && <FileText className="w-4 h-4 text-cyan-400" />}
          {/* Section 2 icons */}
          {subsectionId === '2.1' && <TrendingUp className="w-4 h-4 text-blue-400" />}
          {subsectionId === '2.2' && <AlertTriangle className="w-4 h-4 text-emerald-400" />}
          {subsectionId === '2.3' && <Building2 className="w-4 h-4 text-purple-400" />}
          {subsectionId === '2.4' && <LineChart className="w-4 h-4 text-amber-400" />}
          {subsectionId === '2.5' && <DollarSign className="w-4 h-4 text-cyan-400" />}
          {subsectionId === '2.6' && <BarChart3 className="w-4 h-4 text-rose-400" />}
          {subsectionId === '2.7' && <FileText className="w-4 h-4 text-indigo-400" />}
          {/* Section 3 icons */}
          {subsectionId === '3A' && <TrendingUp className="w-4 h-4 text-blue-400" />}
          {subsectionId === '3B' && <BarChart3 className="w-4 h-4 text-emerald-400" />}
          {subsectionId === '3C' && <DollarSign className="w-4 h-4 text-purple-400" />}
          {subsectionId === '3D' && <LineChart className="w-4 h-4 text-amber-400" />}
          {subsectionId === '3E' && <FileText className="w-4 h-4 text-cyan-400" />}
          {subsectionId === '3F' && <Lightbulb className="w-4 h-4 text-rose-400" />}
        </div>
        <h3 className="text-lg font-semibold text-slate-100" style={{ fontFamily: 'var(--font-display)' }}>
          {subsection.id}. {subsection.name}
        </h3>
      </div>

      {/* 1.1: Company Highlights */}
      {subsectionId === '1.1' && subsection.companies && (
        <CompanyHighlights
          companies={subsection.companies}
          narratives={narratives.company_highlights as Record<string, string>}
        />
      )}

      {/* 1.2: KPI Table */}
      {subsectionId === '1.2' && subsection.table && (
        <div className="space-y-4">
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4">
            <DataTable columns={subsection.table.columns} rows={subsection.table.rows} />
          </div>
          {/* Narrative */}
          {narrativeText && (
            <div className="bg-slate-800/30 rounded-lg p-4">
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                {narrativeText}
              </p>
            </div>
          )}
          {/* Key Insights */}
          {Array.isArray(narratives.key_insights) && narratives.key_insights.length > 0 && (
            <ul className="space-y-2">
              {(narratives.key_insights as string[]).map((insight, idx) => (
                <li key={idx} className="flex items-start gap-2 text-slate-300 text-sm">
                  <ChevronRight className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span>{String(insight)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 1.3: Performance Data */}
      {subsectionId === '1.3' && subsection.performance_data && (
        <div className="space-y-4">
          <div className="bg-slate-900/50 border border-slate-700/50 rounded-xl p-4">
            <PerformanceTable data={subsection.performance_data} />
          </div>
          {/* Narrative */}
          {narrativeText.length > 0 ? (
            <div className="bg-slate-800/30 rounded-lg p-4">
              <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                {String(narrativeText)}
              </p>
            </div>
          ) : null}
          {/* Period Highlights */}
          {narratives.period_highlights && typeof narratives.period_highlights === 'object' ? (
            <div className="grid md:grid-cols-3 gap-4">
              {Object.entries(narratives.period_highlights as Record<string, string>).map(([key, value]) => (
                <div key={key} className="bg-slate-800/30 rounded-lg p-4">
                  <h5 className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-2">
                    {key.replace(/_/g, ' ')}
                  </h5>
                  <p className="text-sm text-slate-300">{String(value)}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* 1.4: Charts */}
      {subsectionId === '1.4' && subsection.charts && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {subsection.charts.map((chart) => (
              <Chart
                key={chart.id}
                title={chart.title}
                description={chart.description}
                plotlyConfig={chart.plotly_config}
              />
            ))}
          </div>
          {/* Chart Insights */}
          {narratives.chart_insights && typeof narratives.chart_insights === 'object' ? (
            <div className="bg-slate-800/30 rounded-lg p-4 space-y-3">
              {Object.entries(narratives.chart_insights as Record<string, string>).map(([chartId, insight]) => (
                <div key={chartId}>
                  <h5 className="text-xs font-medium text-slate-400 capitalize mb-1">
                    {chartId.replace(/_/g, ' ')}
                  </h5>
                  <p className="text-sm text-slate-300">{String(insight)}</p>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      )}

      {/* 1.5: Executive Insights - Rendered at top level, skip here */}
      {subsectionId === '1.5' && (
        <p className="text-slate-500 text-sm italic">Executive insights are displayed above.</p>
      )}

      {/* ================================================================== */}
      {/* SECTION 02: Company Classification & Factor Tags */}
      {/* ================================================================== */}

      {/* 2.1: Market Cap & Investment Style */}
      {subsectionId === '2.1' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* 2.2: Financial Health & Leverage */}
      {subsectionId === '2.2' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* 2.3: Business Model & Competitive Positioning */}
      {subsectionId === '2.3' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* 2.4: Sector & Cyclical Classification */}
      {subsectionId === '2.4' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* 2.5: Valuation Buckets & Quality */}
      {subsectionId === '2.5' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* 2.6: Visual Classification (Charts) */}
      {subsectionId === '2.6' && subsection.charts && (
        <div className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            {subsection.charts.map((chart) => (
              <Chart
                key={chart.id}
                title={chart.title}
                description={chart.description}
                plotlyConfig={chart.plotly_config}
              />
            ))}
          </div>
        </div>
      )}

      {/* 2.7: Classification Summary - Key insights displayed at top */}
      {subsectionId === '2.7' && (
        <Section02Subsection narratives={narratives} subsection={subsection} />
      )}

      {/* Section 03: Financial Overview & Historical Analysis */}
      {subsectionId.startsWith('3') && (
        <Section03Subsection narratives={narratives} subsection={subsection} />
      )}
    </div>
  );
}

// ============================================================================
// Main Article Viewer Component
// ============================================================================
export default function ArticleViewer(): ReactElement {
  const { id } = useParams<{ id: string }>();
  const articleId = parseInt(id || '0', 10);

  const { data: article, isLoading, error } = useQuery<ArticleContent>({
    queryKey: ['article', articleId],
    queryFn: async () => {
      const response = await articlesAPI.getArticle(articleId);
      return response.data;
    },
    enabled: articleId > 0,
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-slate-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-500/10 flex items-center justify-center">
            <FileText className="w-8 h-8 text-red-400" />
          </div>
          <h3 className="text-lg font-medium text-slate-200 mb-2">Article not found</h3>
          <Link
            to="/articles"
            className="inline-flex items-center gap-2 px-4 py-2 bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Articles
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      <div className="fixed inset-0 opacity-40 pointer-events-none">
        <div className="absolute inset-0 grid-pattern" />
      </div>

      <div className="relative max-w-5xl mx-auto px-6 py-8">
        {/* Back Link */}
        <Link
          to="/articles"
          className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-slate-300 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Articles
        </Link>

        {/* Article Header */}
        <header className="mb-10">
          <div className="flex items-center gap-2 mb-4">
            {article.tickers.map((ticker) => (
              <Link
                key={ticker}
                to={`/stocks/${ticker}`}
                className="px-2.5 py-1 text-xs font-mono text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded hover:bg-amber-500/20 transition-colors"
              >
                {ticker}
              </Link>
            ))}
          </div>

          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3 leading-tight" style={{ fontFamily: 'var(--font-display)' }}>
            {article.title}
          </h1>

          {article.subtitle && (
            <p className="text-lg text-slate-400 mb-6">{article.subtitle}</p>
          )}

          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              <span>{article.time_ago}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <Cpu className="w-4 h-4" />
              <span className="capitalize">{article.provider}</span>
              <span className="text-slate-600">•</span>
              <span className="font-mono text-xs">{article.model}</span>
            </div>
          </div>
        </header>

        {/* Executive Summary */}
        {article.executive_summary && (
          <section className="mb-10">
            <div className="bg-slate-900/60 border border-slate-700/50 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-400" />
                Executive Summary
              </h2>
              <div className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                {article.executive_summary}
              </div>
            </div>
          </section>
        )}

        {/* Key Takeaways */}
        {article.key_takeaways && article.key_takeaways.length > 0 && (
          <section className="mb-10">
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-emerald-400" />
                Key Takeaways
              </h2>
              <ul className="space-y-3">
                {article.key_takeaways.map((takeaway, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-500/20 text-emerald-400 flex items-center justify-center text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <span className="text-slate-300 leading-relaxed">{takeaway}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Strategic Outlook */}
        {article.strategic_outlook && (
          <section className="mb-10">
            <div className="bg-blue-500/5 border border-blue-500/20 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-400" />
                Strategic Outlook
              </h2>
              <div className="text-slate-300 leading-relaxed whitespace-pre-wrap">
                {article.strategic_outlook}
              </div>
            </div>
          </section>
        )}

        {/* Risk Factors */}
        {article.risk_factors && article.risk_factors.length > 0 && (
          <section className="mb-10">
            <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                Risk Factors
              </h2>
              <ul className="space-y-3">
                {article.risk_factors.map((risk, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 rounded-full bg-red-500/20 text-red-400 flex items-center justify-center text-xs font-semibold">
                      {idx + 1}
                    </span>
                    <span className="text-slate-300 leading-relaxed">{risk}</span>
                  </li>
                ))}
              </ul>
            </div>
          </section>
        )}

        {/* Detailed Subsections */}
        {article.subsections && article.subsections.length > 0 && (
          <section className="mb-10">
            <h2 className="text-xl font-semibold text-slate-100 mb-6" style={{ fontFamily: 'var(--font-display)' }}>
              Detailed Analysis
            </h2>
            {article.subsections
              .filter((sub) => sub.id !== '1.5') // Skip 1.5, already rendered above
              .map((subsection) => (
                <SubsectionRenderer key={subsection.id} subsection={subsection as SubsectionData} />
              ))}
          </section>
        )}

        {/* Footer */}
        <footer className="pt-6 border-t border-slate-800">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <div>
              Generated: {article.generated_at ? new Date(article.generated_at).toLocaleString() : 'Unknown'}
            </div>
            <div className="flex items-center gap-1.5">
              <Cpu className="w-4 h-4" />
              <span className="capitalize">{article.provider}</span>
              <span className="font-mono text-xs text-slate-600">{article.model}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
